import { CartStore } from "assets/cart";
import { CardLineItemCardContainerBlock } from "types/blocks";

const initLineItemCard = ($el: HTMLElement, lineItemKey: string) => {
  const random_id = utils.shortUUID();

  const cart = Alpine.store("cart") as CartStore;

  const lineItem = Shopify.designMode
    ? cart.state.items.find((item) => item.key === lineItemKey) ??
      cart.state.items.find((item) => !!item?.properties?._p_id) ??
      cart.state.items.find((item) => !item?.properties?._p_id_link && !item?.properties?._p_hidden) ??
      cart.state.items[0]
    : cart.state.items.find((item) => item.key === lineItemKey);

  const children = lineItem?.properties?._p_id
    ? cart.state.items
        .filter((child) => child?.properties?._p_id_link === lineItem?.properties?._p_id && child.quantity)
        ?.map((child) => {
          let product = _products[child?.handle];

          if (!product) {
            _product.getProductData(child?.handle, child?.product_id).then((prod) => {
              const index = state.children.findIndex((line) => line.line_item?.key === child?.key);
              if (index !== -1) {
                state.children[index] = {
                  line_item: child,
                  product: prod,
                  variant: prod?.variants?.find((variant) => variant.id === child?.variant_id),
                };
              }
            });
          }
          product ??= _products[child?.handle];

          const variant = product?.variants?.find((variant) => variant.id === child?.variant_id);

          return {
            line_item: child,
            product,
            variant,
          };
        })
    : [];

  let product = _products[lineItem?.handle];

  if (!product) {
    _product.getProductData(lineItem?.handle, lineItem?.product_id).then((prod) => {
      state.product = prod;
      state.variant = prod?.variants?.find((variant) => variant.id === lineItem?.variant_id);
      state.hydrated = true;
    });
  }
  product ??= _products[lineItem?.handle];

  const variant = product?.variants?.find((variant) => variant.id === lineItem?.variant_id);

  const selling_plan = lineItem?.selling_plan_allocation?.selling_plan ?? variant?.selling_plan_allocations?.[0]?.selling_plan;

  const selling_plan_discount_wording = selling_plan?.price_adjustments?.[0]?.value
    ? selling_plan?.price_adjustments?.[0]?.value_type === "fixed_amount"
      ? `${utils.formatMoney(selling_plan?.price_adjustments?.[0]?.value * lineItem.quantity)}`
      : selling_plan?.price_adjustments?.[0]?.value_type === "percentage"
      ? `${selling_plan?.price_adjustments?.[0]?.value}%`
      : ""
    : "";

  const state = window.Alpine.reactive({
    random_id,
    subscriptionIsChanging: false,
    hydrated: !!variant,
    line_item: lineItem,
    product: product,
    variant: variant,
    selling_plan_discount_wording,
    url: lineItem?.url,
    children: [...children],
    price: lineItem?.final_price,
    compare_at_price: Math.max(
      lineItem?.original_price ?? 0,
      variant?.compare_at_price ?? 0,
      lineItem?.selling_plan_allocation?.compare_at_price ?? 0
    ),
    bundle_price:
      lineItem?.final_price +
      (children?.reduce(
        (acc, child) => (acc += child?.line_item?.final_price * (child?.line_item?.quantity / lineItem.quantity) || 0),
        0
      ) || 0),
    bundle_compare_at_price:
      Math.max(
        lineItem?.original_price ?? 0,
        variant?.compare_at_price ?? 0,
        lineItem?.selling_plan_allocation?.compare_at_price ?? 0
      ) +
      (children?.reduce(
        (acc, child) =>
          (acc += Math.max(
            child?.line_item?.original_price * (child?.line_item?.quantity / lineItem.quantity) || 0,
            child?.variant?.compare_at_price * (child?.line_item?.quantity / lineItem.quantity) || 0,
            child?.line_item?.selling_plan_allocation?.compare_at_price * (child?.line_item?.quantity / lineItem.quantity) || 0
          )),
        0
      ) || 0),
    quantity_limit: !children?.length
      ? Math.min(
          variant?.inventory_management === "shopify" && variant?.inventory_policy === "deny"
            ? variant?.inventory_quantity -
                cart.state.items.reduce(
                  (acc, line) => (acc += line.variant_id === variant?.id && line.key !== lineItem.key ? line.quantity : 0),
                  0
                )
            : variant?.preorder && variant?.preorder_quantity
            ? variant?.inventory_quantity +
              variant?.preorder_quantity -
              cart?.state?.items?.reduce((acc, line) => (line.variant_id === variant.id ? acc + line.quantity : acc), 0)
            : 99999
        )
      : Math.min(
          variant?.inventory_management === "shopify" && variant?.inventory_policy === "deny"
            ? variant?.inventory_quantity
            : 9999,
          ...children.map((child) => {
            const quantity_ratio = child.line_item.quantity / lineItem?.quantity;

            return child?.variant?.inventory_management === "shopify" && child?.variant?.inventory_policy === "deny"
              ? Math.floor(
                  (child?.variant?.inventory_quantity -
                    cart.state.items.reduce(
                      (acc, line) =>
                        (acc += line.variant_id === child?.variant?.id && line.key !== child.line_item.key ? line.quantity : 0),
                      0
                    )) /
                    quantity_ratio
                )
              : 9999;
          })
        ),
  });

  const getDynamicValue = (content: string) => {
    return utils.getBracketInputDynamicValue(content, state);
  };

  const getDynamicValueWithFallbacks = (content: string) => {
    return content.split(",")?.reduce((acc, item) => {
      acc ||= utils.getBracketInputDynamicValue(item.trim(), state);
      return acc;
    }, "");
  };

  const getDynamicText = (content: string) => {
    return utils.getBracketInputDynamicPluralizedText(content, state);
  };

  const getContentLabels = (key: string) => {
    const labels = key.split(".").reduce<any>((acc, selector) => {
      if (!selector || acc === undefined || acc === null) return acc || "";

      if (acc && selector in acc) {
        return acc[selector];
      }
      return "";
    }, state);

    if (Array.isArray(labels)) {
      return labels.filter(Boolean);
    }
    return [labels].filter(Boolean);
  };

  const setSubscription = async (selling_plan_id: number) => {
    state.subscriptionIsChanging = true;
    const p_id = state.line_item?.properties?._p_id;
    const key = state?.line_item?.key;
    const quantity = state?.line_item?.quantity;

    const getUpdateAbleChildren = async (id: string) => {
      const children = [];
      for (const child of cart.state.items) {
        if (child?.properties?._p_id_link !== id) continue;
        let product = _products[child?.handle];

        if (!product) {
          await _product.getProductData(child?.handle, child?.product_id);
        }
        product ??= _products[child?.handle];

        const variant = product?.variants?.find((variant) => variant.id === child?.variant_id);
        if (
          (!selling_plan_id && child.selling_plan_allocation?.selling_plan?.id) ||
          (variant?.selling_plan_allocations?.some((plan) => plan?.selling_plan?.id === selling_plan_id) &&
            child.selling_plan_allocation?.selling_plan?.id !== selling_plan_id)
        ) {
          children.push({
            line_item: child,
            product,
            variant,
          });
        }
      }
      return children;
    };

    if (p_id) {
      while ((await getUpdateAbleChildren(p_id))?.length) {
        const firstChild = (await getUpdateAbleChildren(p_id))[0];
        await _cart.change({
          id: firstChild.line_item.key,
          selling_plan: +selling_plan_id,
          quantity: firstChild?.line_item.quantity,
        });
      }
      const mainItem = cart.state.items?.find((item) => item?.properties?._p_id === p_id);
      await _cart.change({ id: mainItem.key, selling_plan: +selling_plan_id, quantity: mainItem.quantity });
    }

    if (!p_id) {
      await _cart.change({ id: key, selling_plan: +selling_plan_id, quantity: quantity });
    }

    state.subscriptionIsChanging = false;
  };

  const hideIfEmpty = (element: HTMLElement, hide_if_empty: "none" | "section" | "container" | "block", isEmpty = false) => {
    switch (hide_if_empty) {
      case "none":
        break;
      case "section":
        element.closest(`.shopify-section`)?.classList.toggle("!hidden", isEmpty);
        break;
      case "container":
        element
          .closest(`[data-style-id]:not([data-style-id="${element.getAttribute("data-style-id")}"])`)
          ?.classList.toggle("!hidden", isEmpty);
        break;
      case "block":
        break;
    }
  };

  const showConditionally = (condition: CardLineItemCardContainerBlock["settings"]["show_conditionally"]) => {
    switch (condition) {
      case "no_addon_products":
        return !state.children?.length;
      case "addon_products":
        return !!state.children?.length;
      case "always": {
        return true;
      }
      case "with_subscriptions": {
        return !!state.line_item?.selling_plan_allocation?.selling_plan?.id;
      }
      case "no_subscriptions": {
        return !state.line_item?.selling_plan_allocation?.selling_plan?.id;
      }
      case "with_variants": {
        return state?.product?.variants?.length > 1;
      }
      case "no_variants": {
        return state?.product?.variants?.length <= 1;
      }
    }
  };

  Alpine.effect(() => {
    const lineItem = Shopify.designMode
      ? cart.state.items.find((item) => item.key === lineItemKey) ??
        cart.state.items.find((item) => !!item?.properties?._p_id) ??
        cart.state.items.find((item) => !item?.properties?._p_id_link && !item?.properties?._p_hidden) ??
        cart.state.items[0]
      : cart.state.items.find((item) => item.key === lineItemKey) ?? state.line_item;

    if (!utils.deepEqual(lineItem, state.line_item)) {
      state.line_item = lineItem;
    }
  });

  Alpine.effect(() => {
    const selling_plan =
      state?.line_item?.selling_plan_allocation?.selling_plan ?? state?.variant?.selling_plan_allocations?.[0]?.selling_plan;

    state.selling_plan_discount_wording = selling_plan?.price_adjustments?.[0]?.value
      ? selling_plan?.price_adjustments?.[0]?.value_type === "fixed_amount"
        ? `${utils.formatMoney(selling_plan?.price_adjustments?.[0]?.value * state?.line_item?.quantity)}`
        : selling_plan?.price_adjustments?.[0]?.value_type === "percentage"
        ? `${selling_plan?.price_adjustments?.[0]?.value}%`
        : ""
      : "";
  });

  Alpine.effect(() => {
    if (!state.line_item) return;
    state.children = state?.line_item?.properties?._p_id
      ? cart.state.items
          .filter((child) => child?.properties?._p_id_link === state?.line_item?.properties?._p_id && child.quantity)
          ?.map((child) => {
            let product = _products[child?.handle];

            if (!product) {
              _product.getProductData(child?.handle, child?.product_id).then((prod) => {
                const index = state.children.findIndex((line) => line.line_item?.key === child?.key);
                if (index !== -1) {
                  state.children[index] = {
                    line_item: child,
                    product: prod,
                    variant: prod?.variants?.find((variant) => variant.id === child?.variant_id),
                  };
                }
              });
            }
            product ??= _products[child?.handle];

            const variant = product?.variants?.find((variant) => variant.id === child?.variant_id);

            return {
              line_item: child,
              product,
              variant,
            };
          })
      : [];

    state.price = state.line_item?.final_price;
    state.compare_at_price = Math.max(
      state.line_item?.original_price ?? 0,
      state.variant?.compare_at_price ?? 0,
      state.line_item?.selling_plan_allocation?.compare_at_price ?? 0
    );
    state.bundle_price =
      state.line_item?.final_price +
      (state.children?.reduce(
        (acc, child) => (acc += child?.line_item?.final_price * (child?.line_item?.quantity / state.line_item.quantity) || 0),
        0
      ) || 0);
    state.bundle_compare_at_price =
      Math.max(
        state.line_item?.original_price ?? 0,
        state.variant?.compare_at_price ?? 0,
        state.line_item?.selling_plan_allocation?.compare_at_price ?? 0
      ) +
      (state.children?.reduce(
        (acc, child) =>
          (acc += Math.max(
            child?.line_item?.original_price * (child?.line_item?.quantity / state.line_item.quantity) || 0,
            child?.variant?.compare_at_price * (child?.line_item?.quantity / state.line_item.quantity) || 0,
            child?.line_item?.selling_plan_allocation?.compare_at_price *
              (child?.line_item?.quantity / state.line_item.quantity) || 0
          )),
        0
      ) || 0);

    state.quantity_limit = !state.children?.length
      ? Math.min(
          state.variant?.inventory_management === "shopify" && state.variant?.inventory_policy === "deny"
            ? state.variant?.inventory_quantity -
                cart.state.items.reduce(
                  (acc, line) =>
                    (acc += line.variant_id === state.variant?.id && line.key !== state?.line_item?.key ? line.quantity : 0),
                  0
                )
            : state.variant?.preorder && state.variant?.preorder_quantity
            ? state.variant?.inventory_quantity +
              state.variant?.preorder_quantity -
              cart?.state?.items?.reduce((acc, line) => (line.variant_id === state.variant.id ? acc + line.quantity : acc), 0)
            : 99999
        )
      : Math.min(
          state.variant?.inventory_management === "shopify" && state.variant?.inventory_policy === "deny"
            ? state.variant?.inventory_quantity
            : 9999,
          ...state.children.map((child) => {
            const quantity_ratio = child.line_item.quantity / state.line_item?.quantity;

            return child?.variant?.inventory_management === "shopify" && child?.variant?.inventory_policy === "deny"
              ? Math.floor(
                  (child?.variant?.inventory_quantity -
                    cart.state.items.reduce(
                      (acc, line) =>
                        (acc += line.variant_id === child?.variant?.id && line.key !== child.line_item.key ? line.quantity : 0),
                      0
                    )) /
                    quantity_ratio
                )
              : 9999;
          })
        );
  });

  return {
    line_card: state,
    $el,
    showConditionally,
    setSubscription,
    getDynamicValue,
    getDynamicValueWithFallbacks,
    getDynamicText,
    getContentLabels,
    hideIfEmpty,
  };
};

const fallbackHydrationQueue = new Set<HTMLElement>();

const hydrateLineItemCardContents = ($el: HTMLElement) => {
  const card_handle = $el.getAttribute(`data-card-handle`);
  const line_item_key = $el.getAttribute(`data-line-item-key`);
  const classes = utils.JSONParse<string[]>($el.getAttribute(`data-card-classes`) ?? "[]");

  const node = document.querySelector(`[data-line-item-card='${card_handle}']`)?.cloneNode(true) as HTMLElement;

  if (!node) {
    fallbackHydrationQueue.add($el);
    return;
  }

  node?.removeAttribute(`data-line-item-card`);
  node?.setAttribute("data-line-item-key", line_item_key ?? "");
  node.querySelectorAll("[data-loop-item], [data-x-if], style").forEach((el) => {
    el.remove();
  });
  node.classList.add(...classes);
  $el.parentNode?.replaceChild(node, $el);
};

document.addEventListener("modalsLoaded", () => {
  fallbackHydrationQueue.forEach((el) => {
    hydrateLineItemCardContents(el);
  });
  fallbackHydrationQueue.clear();
});

const observer = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting && entry.target instanceof HTMLElement) {
        hydrateLineItemCardContents(entry.target);
        observer.unobserve(entry.target);
      }
    }
  },
  { rootMargin: "300px 300px 300px 300px" }
);

const hydrateLineItemCard = ($el: HTMLElement) => {
  if ($el.hasAttribute("data-intersected")) {
    hydrateLineItemCardContents($el);
    return;
  }

  observer.observe($el);
};

export type InitLineItemCard = typeof initLineItemCard;
export type HydrateLineItemCard = typeof hydrateLineItemCard;

window._sections["initLineItemCard"] = initLineItemCard;
window._sections["hydrateLineItemCard"] = hydrateLineItemCard;
